import { useState } from 'react'
import { PageHeader, Card, Badge, DataTable, TableColumn, Input, Select, Button } from '@pilot-hub/ui'

export function AirspaceObservationPage() {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')

  // Mock data for airspace objects
  const airspaceObjects = [
    { id: 1, type: 'Drohne', name: 'DJI Mavic 3E', altitude: 85, distance: 1.2, bearing: 45, relativeSpeed: 5, risk: 'low' },
    { id: 2, type: 'Hubschrauber', name: 'ADAC Luftrettung', altitude: 350, distance: 3.5, bearing: 120, relativeSpeed: 120, risk: 'medium' },
    { id: 3, type: 'Flugzeug', name: 'Cessna 172', altitude: 1200, distance: 8.5, bearing: 270, relativeSpeed: 180, risk: 'low' },
    { id: 4, type: 'Drohne', name: 'Unbekannt', altitude: 45, distance: 0.8, bearing: 15, relativeSpeed: 0, risk: 'high' },
    { id: 5, type: 'Vogel', name: 'Schwarm', altitude: 30, distance: 0.3, bearing: 320, relativeSpeed: 15, risk: 'medium' },
  ]

  const columns: TableColumn[] = [
    { key: 'type', header: 'Typ', accessor: 'type', render: (o: any) => <Badge variant={getTypeVariant(o.type)}>{o.type}</Badge> },
    { key: 'name', header: 'Objekt', accessor: 'name' },
    { key: 'altitude', header: 'Höhe (m)', accessor: 'altitude', render: (o: any) => <span className="font-mono">{o.altitude}</span> },
    { key: 'distance', header: 'Distanz (km)', accessor: 'distance', render: (o: any) => <span className="font-mono">{o.distance}</span> },
    { key: 'bearing', header: 'Kurs (°)', accessor: 'bearing', render: (o: any) => <span className="font-mono">{o.bearing}°</span> },
    { key: 'relativeSpeed', header: 'Rel. Speed (km/h)', accessor: 'relativeSpeed', render: (o: any) => <span className="font-mono">{o.relativeSpeed}</span> },
    { key: 'risk', header: 'Risiko', accessor: 'risk', render: (o: any) => <Badge variant={getRiskVariant(o.risk)}>{o.risk}</Badge> },
  ]

  function getTypeVariant(type: string) {
    switch (type) {
      case 'Drohne': return 'info'
      case 'Hubschrauber': return 'warning'
      case 'Flugzeug': return 'secondary'
      case 'Vogel': return 'success'
      default: return 'secondary'
    }
  }

  function getRiskVariant(risk: string) {
    switch (risk) {
      case 'high': return 'danger'
      case 'medium': return 'warning'
      case 'low': return 'success'
      default: return 'info'
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Luftraum-Beobachtung"
        subtitle="Überwachung des umliegenden Luftraums auf Konflikte"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Objekte im Luftraum</p>
          <p className="text-3xl font-bold text-blue-400">5</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Kritische Konflikte</p>
          <p className="text-3xl font-bold text-red-400">1</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Warnungen</p>
          <p className="text-3xl font-bold text-yellow-400">2</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Sicher</p>
          <p className="text-3xl font-bold text-green-400">2</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="card-header flex items-center justify-between">
            <div className="card-title">Luftraum-Objekte</div>
            <div className="flex gap-2">
              <Input
                placeholder="Suchen..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-64"
              />
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                options={[
                  { value: 'all', label: 'Alle' },
                  { value: 'high', label: 'Hohes Risiko' },
                  { value: 'medium', label: 'Mittel' },
                  { value: 'low', label: 'Niedrig' },
                ]}
                className="w-40"
              />
            </div>
          </div>
          <div className="card-content">
            <DataTable
              data={airspaceObjects}
              columns={columns}
              keyAccessor={(o) => o.id.toString()}
              emptyMessage="Keine Objekte im Luftraum"
            />
          </div>
        </Card>

        <Card>
          <div className="card-header">
            <div className="card-title">Konflikt-Warnungen</div>
          </div>
          <div className="card-content space-y-3">
            <div className="p-3 bg-red-900/30 border border-red-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-red-400">KOLLISIONSGEFAHR</p>
                  <p className="text-sm text-red-300">Unbekannte Drohne auf Kollisionskurs</p>
                </div>
                <Badge variant="danger">KRITISCH</Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded">
                  <p className="text-gray-500 dark:text-gray-400">Objekt</p>
                  <p className="font-mono">Unbekannte Drohne</p>
                </div>
                <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded">
                  <p className="text-gray-500 dark:text-gray-400">Distanz</p>
                  <p className="font-mono">0.8 km</p>
                </div>
                <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded">
                  <p className="text-gray-500 dark:text-gray-400">Höhe</p>
                  <p className="font-mono">45 m</p>
                </div>
                <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded">
                  <p className="text-gray-500 dark:text-gray-400">Kurs</p>
                  <p className="font-mono">15°</p>
                </div>
                <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded">
                  <p className="text-gray-500 dark:text-gray-400">Rel. Geschwindigkeit</p>
                  <p className="font-mono">0 km/h (schwebend)</p>
                </div>
                <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded">
                  <p className="text-gray-500 dark:text-gray-400">Zeit bis CPA</p>
                  <p className="font-mono text-red-400">12 Sekunden</p>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <Button variant="danger" size="sm">Ausweichmanöver einleiten</Button>
                <Button variant="secondary" size="sm">Luftraum melden</Button>
              </div>
            </div>

            <div className="p-3 bg-yellow-900/30 border border-yellow-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-yellow-400">LUFTRAUM-VERLETZUNG</p>
                  <p className="text-sm text-yellow-300">ADAC Hubschrauber in Sicherheitszone</p>
                </div>
                <Badge variant="warning">WARNUNG</Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded">
                  <p className="text-gray-500 dark:text-gray-400">Objekt</p>
                  <p className="font-mono">ADAC Hubschrauber</p>
                </div>
                <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded">
                  <p className="text-gray-500 dark:text-gray-400">Distanz</p>
                  <p className="font-mono">3.5 km</p>
                </div>
                <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded">
                  <p className="text-gray-500 dark:text-gray-400">Höhe</p>
                  <p className="font-mono">350 m</p>
                </div>
                <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded">
                  <p className="text-gray-500 dark:text-gray-400">Rel. Speed</p>
                  <p className="font-mono">120 km/h</p>
                </div>
              </div>
            </div>

            <div className="p-3 bg-green-900/30 border border-green-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-green-400">KEIN KONFLIKT</p>
                  <p className="text-sm text-green-300">Cessna 172 auf sicherer Route</p>
                </div>
                <Badge variant="success">SICHER</Badge>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <Card>
        <div className="card-header">
          <div className="card-title">Luftraum-Zonen</div>
        </div>
        <div className="card-content space-y-2">
          {[
            { name: 'ED-R-123 Sperrzone', type: 'restricted', altitude: 'SFC - 2000ft', status: 'active' },
            { name: 'ED-D-456 Gefahrenzone', type: 'danger', altitude: 'SFC - 3000ft', status: 'active' },
            { name: 'ED-R-789 Sperrzone', type: 'restricted', altitude: '1000ft - 5000ft', status: 'inactive' },
          ].map(zone => (
            <div key={zone.name} className="flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <div>
                <p className="font-medium">{zone.name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{zone.altitude} • {zone.type === 'restricted' ? 'Sperrzone' : 'Gefahrenzone'}</p>
              </div>
              <Badge variant={zone.status === 'active' ? 'danger' : 'success'}>{zone.status}</Badge>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}